import { useState, useEffect, useCallback } from "react";
import { phonenumbersAPI } from "../services/api";
import { TEST_PHONE_NUMBER, formatPhoneDisplay } from "../lib/phoneUtils";

function toIdString(v) {
  if (v == null || v === "") return null;
  if (typeof v === "object" && v?.$oid) return String(v.$oid);
  return String(v).trim() || null;
}

/**
 * Returns the list of phone numbers to show in Inbound/Outbound/Phone/SMS/BatchCall:
 * - User's numbers from GET /api/phonenumbers (MongoDB), including saved inbound/outbound script, file, voice.
 * - One shared test number (with isTest: true) so users without numbers can still try.
 * Each item: { _id, number, display, friendlyName?, isTest, inboundScriptId?, outboundScriptId?, inboundFileId?, outboundFileId?, voiceId? }.
 */
export function useUserPhoneNumbers() {
  const [userNumbers, setUserNumbers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchNumbers = useCallback(() => {
    setLoading(true);
    setError(null);
    return phonenumbersAPI
      .list()
      .then((res) => {
        const list = res.data?.numbers ?? [];
        return list.map((n) => {
          const rawId = n._id;
          const id = rawId != null ? (typeof rawId === "object" && rawId?.$oid ? rawId.$oid : String(rawId)) : null;
          const num = n.number || n.numberNormalized || "";
          const formatted = formatPhoneDisplay(num) || num;
          const name = n.friendlyName && String(n.friendlyName).trim() ? String(n.friendlyName).trim() : null;
          const isShared = n.ownerId == null || n.ownerId === undefined;
          const display = name ? `${name} — ${formatted}` : formatted;
          return {
            _id: id,
            number: num,
            friendlyName: name,
            display,
            displayNumber: formatted,
            isTest: isShared,
            ownerId: n.ownerId,
            inboundScriptId: toIdString(n.inboundScriptId),
            outboundScriptId: toIdString(n.outboundScriptId),
            inboundFileId: toIdString(n.inboundFileId),
            outboundFileId: toIdString(n.outboundFileId),
            inboundCollectionIds: Array.isArray(n.inboundCollectionIds) ? n.inboundCollectionIds.map(String) : [],
            outboundCollectionIds: Array.isArray(n.outboundCollectionIds) ? n.outboundCollectionIds.map(String) : [],
            voiceId: n.voiceId && String(n.voiceId).trim() ? String(n.voiceId).trim() : null,
          };
        });
      })
      .catch((err) => {
        setError(err.response?.data?.message || err.message || "Failed to load numbers");
        return [];
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetchNumbers().then((list) => {
      if (!cancelled) setUserNumbers(list);
    });
    return () => { cancelled = true; };
  }, [fetchNumbers]);

  const refetch = useCallback(() => {
    return fetchNumbers().then((list) => {
      setUserNumbers(list);
      return list;
    });
  }, [fetchNumbers]);

  const testNumberOption = {
    number: TEST_PHONE_NUMBER,
    display: `Test number — ${formatPhoneDisplay(TEST_PHONE_NUMBER)}`,
    displayNumber: formatPhoneDisplay(TEST_PHONE_NUMBER),
    friendlyName: "Test number",
    isTest: true,
  };

  const hasTestNumber = userNumbers.some((u) => u.number && String(u.number).replace(/\D/g, "") === String(TEST_PHONE_NUMBER).replace(/\D/g, ""));
  const allNumbers = hasTestNumber ? userNumbers : [...userNumbers, testNumberOption];

  return { userNumbers, testNumberOption, allNumbers, loading, error, refetch };
}
